import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import { useChat } from '../../hooks/useChat';
import InsightCard from './components/InsightCard';
import ProfileScoreChart from './components/ProfileScoreChart';
import RecommendationCard from './components/RecommendationCard';
import TrendAnalysis from './components/TrendAnalysis';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/navigation/Header';

const GeminiProfileInsights = () => {
  const navigate = useNavigate();
  const { user, userProfile, creditBalance, signOut } = useAuth();

  const displayName = userProfile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'User';
  const displayEmail = user?.email || '';

  // Robust premium gate: user must have at least 3 credits OR a dedicated is_premium flag.
  // Using a minimum threshold (3) prevents mid-session lockouts caused by a single credit
  // being consumed while the page is open. The is_premium flag (if present on the profile)
  // takes precedence and allows future subscription-based access without credit dependency.
  const PREMIUM_CREDIT_THRESHOLD = 3;
  const isPremium =
    userProfile?.is_premium === true ||
    (typeof creditBalance === 'number' && creditBalance >= PREMIUM_CREDIT_THRESHOLD);

  const [activeCategory, setActiveCategory] = useState('overview');
  const [savedTips, setSavedTips] = useState([]);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [isGeneratingInsights, setIsGeneratingInsights] = useState(false);
  const [insights, setInsights] = useState(null);
  const [fullHistory, setFullHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const { response, isLoading, error, sendMessage } = useChat('GEMINI', 'gemini/gemini-2.5-flash', false);

  // Fetch up to 20 sessions for richer trend analysis
  useEffect(() => {
    if (!isPremium || !user?.id) return;
    const fetchFullHistory = async () => {
      setHistoryLoading(true);
      try {
        const { data, error: fetchError } = await supabase
          ?.from('analysis_history')
          ?.select('id, photo_count, average_score, highest_score, gender_filter, credits_used, created_at, results')
          ?.eq('user_id', user?.id)
          ?.order('created_at', { ascending: false })
          ?.limit(20);
        if (!fetchError && data) setFullHistory(data);
      } catch (err) {
        console.error('Error fetching full history:', err);
      } finally {
        setHistoryLoading(false);
      }
    };
    fetchFullHistory();
  }, [isPremium, user?.id]);

  // Map Supabase snake_case to camelCase for components
  const historyForInsights = fullHistory?.map(session => ({
    date: session?.created_at ? new Date(session?.created_at)?.toISOString()?.split('T')?.[0] : '',
    averageScore: parseFloat(session?.average_score) || 0,
    highestScore: parseFloat(session?.highest_score) || 0,
    photoCount: session?.photo_count || 0
  })) || [];

  useEffect(() => {
    if (!isPremium) {
      setShowUpgradeDialog(true);
    }
  }, [isPremium]);

  // Auto-generate insights once full history is loaded
  useEffect(() => {
    if (isPremium && !historyLoading && historyForInsights?.length > 0 && !insights && !isGeneratingInsights) {
      generateInsights();
    }
  }, [historyLoading, historyForInsights?.length]);

  const generateInsights = async () => {
    if (historyForInsights?.length === 0) return;
    setIsGeneratingInsights(true);

    const prompt = `You are an expert photo attractiveness consultant. Analyze this user's photo ranking history and provide personalized insights.

Analysis History (${historyForInsights?.length} sessions, most recent first):
${historyForInsights?.map(session => `- ${session?.date}: Avg Score ${session?.averageScore?.toFixed(1)}, Highest ${session?.highestScore?.toFixed(1)}, ${session?.photoCount} photos`)?.join('\n')}

Provide a JSON response with:
{
  "overallTrend": "improving/stable/declining",
  "strengthAreas": ["area1", "area2"],
  "improvementAreas": ["area1", "area2"],
  "topRecommendations": [
    {"title": "Short title", "description": "1-2 sentence actionable tip", "impact": "high/medium/low", "category": "lighting/composition/expression/styling"},
    {"title": "Short title", "description": "1-2 sentence actionable tip", "impact": "high/medium/low", "category": "lighting/composition/expression/styling"},
    {"title": "Short title", "description": "1-2 sentence actionable tip", "impact": "high/medium/low", "category": "lighting/composition/expression/styling"}
  ],
  "predictedImprovement": "X-Y points if recommendations implemented",
  "bestPhotoPatterns": ["pattern1", "pattern2"],
  "nextSteps": ["step1", "step2"]
}

Keep all text concise and impactful. Base recommendations on the actual score trends observed.`;

    try {
      await sendMessage([{ role: 'user', content: prompt }]);
    } catch (err) {
      console.error('Error generating insights:', err);
    } finally {
      setIsGeneratingInsights(false);
    }
  };

  useEffect(() => {
    if (response && !isLoading) {
      try {
        // Strip markdown code fences if present
        const cleaned = response?.replace(/```json\n?/g, '')?.replace(/```\n?/g, '')?.trim();
        const parsed = JSON.parse(cleaned);
        setInsights(parsed);
      } catch (err) {
        console.error('Error parsing insights:', err);
      }
    }
  }, [response, isLoading]);

  const categories = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'recommendations', label: 'Recommendations', icon: 'Lightbulb' },
    { id: 'trends', label: 'Trends', icon: 'TrendingUp' },
    { id: 'comparison', label: 'Comparison', icon: 'BarChart3' }
  ];

  const handleSaveTip = (tipId) => {
    setSavedTips(prev =>
      prev?.includes(tipId)
        ? prev?.filter(id => id !== tipId)
        : [...prev, tipId]
    );
  };

  const handleUpgrade = () => {
    navigate('/purchase-credits');
  };

  // Premium gate - show upgrade prompt for free users
  if (!isPremium) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-cyan-50">
        <Header
          user={{ name: displayName, email: displayEmail }}
          onNavigate={(path) => navigate(path)}
          onPurchaseCredits={() => navigate('/purchase-credits')}
          onLogout={signOut}
        />
        <main className="main-content">
          <div className="max-w-4xl mx-auto px-4 py-12 text-center relative">
            {/* Decorative floating icons */}
            <div className="absolute top-8 left-8 md:left-16 animate-float">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Icon name="Brain" size={20} color="#9333EA" />
              </div>
            </div>
            <div className="absolute top-8 right-8 md:right-16 animate-float-delayed">
              <Icon name="Sparkles" size={24} color="#06B6D4" />
            </div>

            {/* Main Icon with Gradient Background */}
            <div className="relative inline-block mb-8">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-200 to-cyan-200 rounded-full blur-2xl opacity-40 scale-110"></div>
              <div className="relative w-32 h-32 md:w-40 md:h-40 rounded-full bg-gradient-to-br from-purple-100 to-cyan-100 flex items-center justify-center">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-purple-600 to-cyan-500 flex items-center justify-center">
                  <Icon name="Sparkles" size={40} color="#FFFFFF" />
                </div>
              </div>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text text-transparent mb-4">
              Unlock AI-Powered Profile Insights
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Get personalized recommendations, trend analysis, and actionable tips to optimize your photo attractiveness scores. Available exclusively for Premium members.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg hover:shadow-xl transition-shadow">
                <div className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <Icon name="Brain" size={24} color="#9333EA" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">AI Analysis</h3>
                <p className="text-sm text-muted-foreground">Deep learning insights from your photo history</p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg hover:shadow-xl transition-shadow">
                <div className="bg-gradient-to-br from-cyan-100 to-cyan-200 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <Icon name="TrendingUp" size={24} color="#0891B2" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">Trend Tracking</h3>
                <p className="text-sm text-muted-foreground">Monitor improvement over time with predictive modeling</p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg hover:shadow-xl transition-shadow">
                <div className="bg-gradient-to-br from-orange-100 to-orange-200 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <Icon name="Target" size={24} color="#F97316" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">Actionable Tips</h3>
                <p className="text-sm text-muted-foreground">Specific guidance tailored to your unique features</p>
              </div>
            </div>

            <button
              onClick={handleUpgrade}
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600 to-cyan-500 text-white font-semibold text-lg rounded-2xl hover:opacity-90 transition-opacity shadow-lg hover:shadow-xl transform hover:scale-105 transition-transform duration-200 mb-4"
            >
              <Icon name="Zap" size={24} color="#FFFFFF" />
              Upgrade to Premium
            </button>
            <p className="text-sm text-muted-foreground">
              Starting at $19.99/month • Cancel anytime
            </p>
          </div>
        </main>

        <ConfirmDialog
          isOpen={showUpgradeDialog}
          onClose={() => {
            setShowUpgradeDialog(false);
            navigate('/dashboard-analysis-history');
          }}
          onConfirm={handleUpgrade}
          title="Premium Feature"
          message="Profile Insights is available exclusively for Premium members. Upgrade now to unlock AI-powered recommendations."
          confirmText="Upgrade Now"
          cancelText="Maybe Later"
          variant="default"
          icon="Crown"
        />
      </div>
    );
  }

  // Premium content
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-cyan-50">
      <Header
        user={{ name: displayName, email: displayEmail }}
        onNavigate={(path) => navigate(path)}
        onPurchaseCredits={() => navigate('/purchase-credits')}
        onLogout={signOut}
      />
      <main className="main-content">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8 relative">
            {/* Decorative floating icons - hidden on mobile */}
            <div className="hidden md:block absolute -top-4 left-1/4 animate-float">
              <Icon name="Sparkles" size={20} color="#9333EA" />
            </div>
            <div className="hidden md:block absolute -top-4 right-1/4 animate-float-delayed">
              <Icon name="Star" size={20} color="#06B6D4" />
            </div>

            <div>
              <div className="flex items-center gap-2 sm:gap-3 mb-2">
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text text-transparent">
                  Profile Insights
                </h1>
                <div className="bg-gradient-to-r from-purple-600 to-cyan-500 text-white text-xs font-semibold px-2 sm:px-3 py-1 rounded-full flex items-center gap-1">
                  <Icon name="Crown" size={12} color="#FFFFFF" />
                  PREMIUM
                </div>
              </div>
              <p className="text-sm sm:text-base text-muted-foreground">
                AI-powered recommendations based on your photo analysis history
                {historyForInsights?.length > 0 && (
                  <span className="ml-2 text-xs text-purple-600 font-medium">
                    ({historyForInsights?.length} sessions analysed)
                  </span>
                )}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={generateInsights}
              iconName="RefreshCw"
              iconPosition="left"
              disabled={isGeneratingInsights || historyLoading || historyForInsights?.length === 0}
              loading={isGeneratingInsights || historyLoading}
              className="w-full sm:w-auto min-h-[44px]"
            >
              Refresh Insights
            </Button>
          </div>

          {/* No history empty state */}
          {!historyLoading && historyForInsights?.length === 0 && (
            <div className="bg-card rounded-xl p-10 border border-border text-center">
              <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
                <Icon name="ImageOff" size={32} color="#9333EA" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">No Analysis History Yet</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Upload and analyse your photos first. Once you have at least one analysis session, Gemini will generate personalised insights and trend data for you.
              </p>
              <Button variant="default" onClick={() => navigate('/photo-upload-interface')}>
                Analyse My Photos
              </Button>
            </div>
          )}

          {/* Category Tabs - Mobile Optimized with Horizontal Scroll */}
          {historyForInsights?.length > 0 && (
            <div className="flex gap-2 mb-6 sm:mb-8 overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0 scrollbar-hide">
              {categories?.map(category => (
                <button
                  key={category?.id}
                  onClick={() => setActiveCategory(category?.id)}
                  className={`flex items-center gap-2 px-4 sm:px-5 py-3 min-h-[44px] rounded-lg font-medium transition-all whitespace-nowrap text-sm sm:text-base ${
                    activeCategory === category?.id
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'bg-card text-muted-foreground hover:bg-muted active:scale-95'
                  }`}
                >
                  <Icon name={category?.icon} size={18} />
                  {category?.label}
                </button>
              ))}
            </div>
          )}

          {/* Loading State */}
          {(historyLoading || isGeneratingInsights || isLoading) && (
            <div className="space-y-6">
              <SkeletonLoader height={200} className="w-full" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <SkeletonLoader height={150} className="w-full" />
                <SkeletonLoader height={150} className="w-full" />
              </div>
            </div>
          )}

          {/* Content */}
          {!historyLoading && !isGeneratingInsights && !isLoading && insights && historyForInsights?.length > 0 && (
            <>
              {activeCategory === 'overview' && (
                <div className="space-y-6">
                  <ProfileScoreChart analysisHistory={historyForInsights} trend={insights?.overallTrend} />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InsightCard
                      title="Your Strengths"
                      icon="CheckCircle"
                      iconColor="#10B981"
                      items={insights?.strengthAreas}
                    />
                    <InsightCard
                      title="Growth Opportunities"
                      icon="Target"
                      iconColor="#F97316"
                      items={insights?.improvementAreas}
                    />
                  </div>

                  <div className="bg-gradient-to-r from-purple-600 to-cyan-500 rounded-xl p-6 text-white">
                    <div className="flex items-start gap-4">
                      <div className="bg-white/20 rounded-full p-3">
                        <Icon name="TrendingUp" size={24} color="#FFFFFF" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2">Predicted Improvement</h3>
                        <p className="text-white/90">
                          Implementing these recommendations could improve your average score by {insights?.predictedImprovement}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeCategory === 'recommendations' && (
                <div className="space-y-4">
                  <div className="bg-card rounded-xl p-6 border border-border mb-6">
                    <h3 className="font-semibold text-foreground mb-2">Top Recommendations</h3>
                    <p className="text-sm text-muted-foreground">
                      Prioritized by potential impact on your attractiveness scores
                    </p>
                  </div>
                  {insights?.topRecommendations?.slice(0, 3)?.map((rec, index) => (
                    <RecommendationCard
                      key={index}
                      recommendation={rec}
                      isSaved={savedTips?.includes(index)}
                      onSave={() => handleSaveTip(index)}
                    />
                  ))}
                </div>
              )}

              {activeCategory === 'trends' && (
                <TrendAnalysis
                  analysisHistory={historyForInsights}
                  patterns={insights?.bestPhotoPatterns}
                  nextSteps={insights?.nextSteps}
                />
              )}

              {activeCategory === 'comparison' && (
                <div className="bg-card rounded-xl p-8 border border-border text-center">
                  <Icon name="BarChart3" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Comparative Analysis
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Compare your performance against attractiveness benchmarks and see where you excel
                  </p>
                  <Button variant="outline" disabled>
                    Coming Soon
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <Icon name="AlertCircle" size={48} color="#DC2626" className="mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-red-900 mb-2">
                Unable to Generate Insights
              </h3>
              <p className="text-red-700 mb-4">
                {error?.message || 'An error occurred while generating your insights'}
              </p>
              <Button variant="outline" onClick={generateInsights}>
                Try Again
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default GeminiProfileInsights;